import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, ArrowLeft, CheckCircle2, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import PasswordStrengthMeter, { isPasswordValid } from '../components/PasswordStrengthMeter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const passwordOk = useMemo(() => isPasswordValid(password), [password]);
  const matches = password.length > 0 && password === confirm;
  const canSubmit = !!token && passwordOk && matches && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!passwordOk) {
      setError('Your password does not meet the strength policy.');
      return;
    }
    if (!matches) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Invalid / missing token state
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="reset-password-page">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center flex flex-col items-center">
          <img
            src="/assets/civilx-tms-logo.png"
            alt="CivilX TMS"
            className="h-14 w-auto object-contain mb-2"
            data-testid="reset-invalid-logo"
          />
          <p className="text-[10px] text-slate-500 uppercase tracking-[0.25em] font-semibold mb-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Dispatch · Track · Deliver</p>
          <AlertCircle className="w-10 h-10 text-red-500 mb-3" />
          <h1
            className="text-2xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: 'Work Sans, sans-serif' }}
            data-testid="reset-missing-token-heading"
          >
            Reset link invalid
          </h1>
          <p
            className="text-sm text-slate-600 mb-5"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            This password reset link is missing a token. It may have been corrupted in your email.
            Request a new one to continue.
          </p>
          <Link
            to="/forgot-password"
            data-testid="reset-request-new-link"
            className="inline-flex items-center justify-center gap-2 bg-[#002FA7] text-white py-2.5 px-5 rounded-md font-semibold text-sm hover:bg-[#00247D]"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="reset-password-page">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <Link
            to="/login"
            data-testid="reset-back-to-login"
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700 mb-6"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>

          <div className="mb-8 flex flex-col items-center text-center">
            <div className="mb-6 flex flex-col items-center">
              <img
                src="/assets/civilx-tms-logo.png"
                alt="CivilX TMS"
                className="h-16 w-auto object-contain"
                data-testid="reset-logo"
              />
              <p className="text-[10px] text-slate-500 uppercase tracking-[0.25em] font-semibold mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Dispatch · Track · Deliver</p>
            </div>
            <h1
              className="text-3xl font-bold tracking-tight text-slate-900 mb-2"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
            >
              Set a new password
            </h1>
            <p
              className="text-sm text-slate-600"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Choose a strong password &mdash; the policy is shown below. The link expires 1 hour after you requested it.
            </p>
          </div>

          {done ? (
            <div
              data-testid="reset-success-message"
              className="p-4 bg-emerald-50 border border-emerald-200 rounded-md"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-emerald-900">Password updated</p>
                  <p className="text-xs text-emerald-800 mt-1">
                    You can now sign in with your new password. Redirecting you to the login page…
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
              {error && (
                <div
                  data-testid="reset-error-message"
                  className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600"
                >
                  {error}
                </div>
              )}

              <div>
                <label
                  htmlFor="password"
                  className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  New password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    id="password"
                    type={reveal ? 'text' : 'password'}
                    required
                    autoFocus
                    data-testid="reset-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    className="w-full pl-10 pr-10 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none transition-all"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  />
                  <button
                    type="button"
                    onClick={() => setReveal((s) => !s)}
                    data-testid="reset-password-toggle-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <PasswordStrengthMeter password={password} testid="reset-password-strength" />
              </div>

              <div>
                <label
                  htmlFor="confirm"
                  className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  Confirm new password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    id="confirm"
                    type={reveal ? 'text' : 'password'}
                    required
                    data-testid="reset-confirm-input"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    disabled={loading}
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none transition-all"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  />
                </div>
                {confirm.length > 0 && !matches && (
                  <p
                    data-testid="reset-mismatch-hint"
                    className="text-xs text-red-600 mt-1.5"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    Passwords do not match.
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="reset-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#002FA7] text-white py-3 px-4 rounded-md font-semibold hover:bg-[#00247D] focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-2 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? 'Updating…' : 'Set new password'}
              </button>
            </form>
          )}
        </div>
      </div>

      <div
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundImage: 'url(/images/login-hero.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-900/40 to-transparent" />
      </div>
    </div>
  );
};

export default ResetPassword;
